import React, { useState, useRef, useCallback, useEffect } from 'react'
import { useMapsLibrary, APIProvider } from '@vis.gl/react-google-maps'
import { TextField, List, ListItem, ListItemText, Paper, Box } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import { OnSelect } from './types'

const useStyles = makeStyles(theme => ({
  container: {
    position: 'relative',
    width: '100%',
  },
  suggestionsContainer: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 1000,
    maxHeight: '300px',
    overflowY: 'auto',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '4px',
    boxShadow: theme.shadows[4],
  },
  suggestionItem: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  suggestionItemActive: {
    backgroundColor: theme.palette.action.selected,
  },
}))

type Props = {
  onSelect: OnSelect
  placeholder?: string
  required?: boolean
  disabled?: boolean
  value?: string
  onChange?: (value: string) => void
  error?: boolean
  helperText?: string
  label?: string
}

type PlacePrediction = google.maps.places.PlacePrediction

const REGION_CODES = ['ar', 'uy', 'cl', 'mx']
const ADDRESS_PRIMARY_TYPES = ['street_address', 'route', 'premise', 'subpremise']
const PLACE_DETAIL_FIELDS = ['formattedAddress', 'location']

const cleanAddressText = (address: string) =>
  address.replace(/Ciudad Autónoma de Buenos Aires/g, 'CABA').replace(/, Argentina$/, '')

const GooglePlacesAutocompleteV2Inner: React.FC<Props> = ({
  onSelect,
  placeholder = 'Buscar dirección...',
  required = false,
  disabled = false,
  value = '',
  onChange,
  error = false,
  helperText,
  label = 'Dirección',
}) => {
  const classes = useStyles()
  const places = useMapsLibrary('places')
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null)
  const requestIdRef = useRef(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const [inputValue, setInputValue] = useState(value)
  const [suggestions, setSuggestions] = useState<PlacePrediction[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  const ensureSessionToken = useCallback(() => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken()
    }
    return sessionTokenRef.current
  }, [])

  const refreshSessionToken = useCallback(() => {
    sessionTokenRef.current = new google.maps.places.AutocompleteSessionToken()
  }, [])

  const handleInputChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value
      setInputValue(newValue)
      setActiveIndex(-1)

      if (!isOpen) {
        onChange?.(newValue)
      }

      if (!places || newValue.length <= 2) {
        setSuggestions([])
        setIsOpen(false)
        return
      }

      requestIdRef.current += 1
      const requestId = requestIdRef.current

      try {
        const {
          suggestions: nextSuggestions,
        } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: newValue,
          includedRegionCodes: REGION_CODES,
          includedPrimaryTypes: ADDRESS_PRIMARY_TYPES,
          sessionToken: ensureSessionToken(),
        })

        if (requestId !== requestIdRef.current) return

        const predictions = nextSuggestions
          .map(suggestion => suggestion.placePrediction)
          .filter((prediction): prediction is PlacePrediction => Boolean(prediction))

        setSuggestions(predictions)
        setIsOpen(predictions.length > 0)
      } catch {
        if (requestId !== requestIdRef.current) return
        setSuggestions([])
        setIsOpen(false)
      }
    },
    [isOpen, onChange, places, ensureSessionToken],
  )

  const handleSuggestionSelect = useCallback(
    async (prediction: PlacePrediction) => {
      const place = prediction.toPlace()

      await place.fetchFields({ fields: PLACE_DETAIL_FIELDS })

      const { location } = place
      if (!location) return

      const toCoord = (coord: number | (() => number)): number =>
        typeof coord === 'function' ? coord() : coord
      const lat = toCoord(location.lat)
      const lng = toCoord(location.lng)

      const shortAddress = prediction.text?.toString() || place.formattedAddress || ''
      const cleanAddress = cleanAddressText(shortAddress)

      setInputValue(cleanAddress)
      setIsOpen(false)
      setSuggestions([])
      onChange?.(cleanAddress)
      onSelect({ lat, lng, address: cleanAddress })
      refreshSessionToken()
    },
    [onSelect, onChange, refreshSessionToken],
  )

  useEffect(() => {
    setInputValue(value)
  }, [value])

  return (
    <Box className={classes.container}>
      <TextField
        ref={inputRef}
        fullWidth
        label={label}
        placeholder={placeholder}
        value={inputValue}
        onChange={handleInputChange}
        onFocus={() => suggestions.length > 0 && setIsOpen(true)}
        required={required}
        disabled={disabled}
        error={error}
        helperText={helperText}
        autoComplete="off"
      />

      {isOpen && suggestions.length > 0 && (
        <Paper className={classes.suggestionsContainer}>
          <List dense>
            {suggestions.map((suggestion, index) => (
              <ListItem
                key={suggestion.placeId}
                className={`${classes.suggestionItem} ${
                  index === activeIndex ? classes.suggestionItemActive : ''
                }`}
                onClick={() => {
                  handleSuggestionSelect(suggestion)
                }}
              >
                <ListItemText
                  primary={suggestion.mainText?.toString() || suggestion.text?.toString()}
                  secondary={suggestion.secondaryText?.toString()}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  )
}

GooglePlacesAutocompleteV2Inner.defaultProps = {
  placeholder: 'Buscar dirección...',
  required: false,
  disabled: false,
  value: '',
  onChange: undefined,
  error: false,
  helperText: undefined,
  label: 'Dirección',
}

const GooglePlacesAutocompleteV2: React.FC<Props> = props => (
  <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string} libraries={['places']}>
    <GooglePlacesAutocompleteV2Inner {...props} />
  </APIProvider>
)

GooglePlacesAutocompleteV2.defaultProps = {
  placeholder: 'Buscar dirección...',
  required: false,
  disabled: false,
  value: '',
  onChange: undefined,
  error: false,
  helperText: undefined,
  label: 'Dirección',
}

export { GooglePlacesAutocompleteV2 }
